import { useCallback, useState } from "react";
import { format, parseISO } from "date-fns";
import {
    useScheduleNativeInvoice,
    useSendNativeInvoice,
} from "@api/native-invoice.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useLockDateState } from "@api/lock.api";
import { isLockedByDateOnly } from "@helpers/lock";
import { NativeInvoiceDraftBody } from "@common/schemas/domains/invoice/native-invoice.schema";
import {
    getNativeInvoiceDueDateError,
    getScheduledSendStatus,
    getSendDisabledTooltip,
    SCHEDULED_SEND_IN_PAST_MESSAGE,
} from "./nativeInvoiceFormHelpers";
import { NativeInvoiceFormState } from "./nativeInvoiceFormTypes";
import { SaveResult } from "./useNativeInvoiceSave";
import { useNativeInvoicePdfDownload } from "./useNativeInvoicePdfDownload";

interface Params {
    workspaceId: string | undefined;
    entityId: number | undefined;
    saver: SaveResult;
    draftBody: NativeInvoiceDraftBody | null;
    toEmails: string[];
    state: NativeInvoiceFormState;
    // Computed invoice total (computeNativeInvoiceTotals). The backend rejects
    // create/update when this is negative (defense-in-depth safeguard shared
    // with the MCP native-invoice tool), so every action that persists the
    // draft is gated on it client-side too.
    total: number;
    onClose: () => void;
    // Invoice date / due date are hidden and read the template's raw stored
    // values in template mode (see DetailsPanel) — they are recomputed from
    // scratch whenever the template is applied to a real invoice, so the
    // due-date-ordering rule can never be user-violated here and must not
    // gate Save.
    isTemplateMode?: boolean;
}

export function useNativeInvoiceHandlers({
    workspaceId,
    entityId,
    saver,
    draftBody,
    toEmails,
    state,
    total,
    onClose,
    isTemplateMode,
}: Params) {
    const { toast, error } = useToaster();
    const [scheduleCheckedAt, setScheduleCheckedAt] = useState(() =>
        Date.now(),
    );
    const sendNativeInvoice = useSendNativeInvoice();
    const scheduleNativeInvoice = useScheduleNativeInvoice();
    const { downloadInvoicePdf, isDownloading } = useNativeInvoicePdfDownload({
        workspaceId,
        entityId,
        invoiceNumber: state.invoiceNumber,
    });

    // Create/Send post journal entries dated on the invoice date, so a date
    // inside a locked accounting period must block them (same period-lock
    // definition useIsInvoiceLocked/LockCell use elsewhere). Save as draft is
    // never gated on this — a draft hasn't posted anything yet. Always called
    // (rules of hooks) but ignored in template mode, where the invoice date
    // isn't user-editable.
    //
    // Fail safe while the lock query is still loading: the lock date is unknown
    // until it resolves, so treat a non-template invoice as locked (mirrors
    // useIsInvoiceLocked). Otherwise Create/Send would be briefly enabled on the
    // first open / entity switch and — since backend enforcement is a follow-up
    // — a quick action could post into the locked period.
    const { lockDate, isResolved: isLockResolved } = useLockDateState(
        entityId ?? null,
    );
    const isInvoiceDateLocked =
        !isTemplateMode &&
        (!isLockResolved ||
            isLockedByDateOnly(lockDate, state.invoiceDate || null));

    const handleDownload = useCallback(async () => {
        if (!workspaceId || entityId === undefined) {
            error("Select a workspace before downloading a PDF");
            return;
        }
        let savedId: string | undefined;
        try {
            savedId = await saver.save();
        } catch {
            error("Couldn't save the invoice, so the PDF wasn't generated");
            return;
        }
        if (!savedId) {
            error("Add invoice details before downloading a PDF");
            return;
        }
        await downloadInvoicePdf(savedId);
    }, [workspaceId, entityId, saver, downloadInvoicePdf, error]);

    const handleSend = useCallback(async () => {
        if (!workspaceId || entityId === undefined) {
            return;
        }
        // Defense in depth: Send/Schedule post a journal entry dated on the
        // invoice date, so refuse to post into a locked period even if the
        // command bar's disabled wiring regresses (backend enforcement is a
        // follow-up). Mirrors handleCreateOnly's guard.
        if (isInvoiceDateLocked) {
            return;
        }
        // Defense in depth: Send/Schedule re-save the draft, and the backend
        // rejects a negative total (see
        // NativeInvoiceValidationInternalService#assertInvoiceTotalNotNegative),
        // so refuse to post even if the command bar's disabled wiring regresses.
        if (total < 0) {
            return;
        }
        if (toEmails.length === 0) {
            error("Add a recipient email before sending");
            return;
        }
        if (!draftBody?.emailSnapshot) {
            error("Email details are required to send");
            return;
        }
        // The draft snapshot persists partial copy, but the send/schedule bodies
        // require a subject, so stop here instead of failing validation.
        if (!draftBody.emailSnapshot.subject) {
            error("Add an email subject before sending");
            return;
        }
        const clickedAt = Date.now();
        const clickedScheduleStatus = getScheduledSendStatus({
            scheduleEnabled: state.scheduleEnabled,
            scheduledSendAt: state.scheduledSendAt,
            now: clickedAt,
        });
        if (clickedScheduleStatus === "past") {
            setScheduleCheckedAt(clickedAt);
            error(SCHEDULED_SEND_IN_PAST_MESSAGE);
            return;
        }
        let savedId: string | undefined;
        try {
            savedId = await saver.save();
        } catch {
            error("Couldn't save the invoice, so it wasn't sent");
            return;
        }
        if (!savedId) {
            error("Add invoice details before sending");
            return;
        }
        try {
            if (state.scheduleEnabled) {
                if (!state.scheduledSendAt) {
                    error("Set a schedule time to schedule send");
                    return;
                }
                await scheduleNativeInvoice.mutateAsync({
                    workspaceId,
                    entityId,
                    invoiceId: savedId,
                    body: {
                        emailSnapshot: draftBody.emailSnapshot,
                        scheduledSendAt: parseISO(state.scheduledSendAt),
                    },
                });
                toast(
                    `Invoice scheduled for ${format(
                        parseISO(state.scheduledSendAt),
                        "MMM d, yyyy 'at' h:mm a",
                    )}`,
                );
            } else {
                await sendNativeInvoice.mutateAsync({
                    workspaceId,
                    entityId,
                    invoiceId: savedId,
                    body: { emailSnapshot: draftBody.emailSnapshot },
                });
                toast(`Invoice sent to ${toEmails.join(", ")}`);
            }
            onClose();
        } catch {
            error("Failed to send invoice");
        }
    }, [
        workspaceId,
        entityId,
        isInvoiceDateLocked,
        total,
        toEmails,
        draftBody,
        saver,
        state.scheduleEnabled,
        state.scheduledSendAt,
        scheduleNativeInvoice,
        sendNativeInvoice,
        toast,
        error,
        onClose,
    ]);

    const scheduledSendStatus = getScheduledSendStatus({
        scheduleEnabled: state.scheduleEnabled,
        scheduledSendAt: state.scheduledSendAt,
        now: scheduleCheckedAt,
    });

    // The due-date rule is enforced server-side, so keep every action that
    // persists the draft disabled while it's violated — otherwise the click
    // fails validation and surfaces as a generic "Save failed" toast that never
    // names the offending field (the inline error on the field is display-only).
    const saveDisabled =
        !isTemplateMode && getNativeInvoiceDueDateError(state) !== undefined;

    // A negative total is composed into the disabled state by the command bar
    // (like the period lock) so it can gate only the actions that persist the
    // draft while leaving the menu caret reachable, so it is intentionally
    // excluded from the tooltip below.
    // Single source of truth for the field-level Send gates: the reason string
    // both drives the disabled state and is shown as the button's tooltip, so
    // the two can't drift apart. Subject is required server-side
    // (z.string().min(1)); keep Send disabled when it's empty so the click
    // can't fail validation.
    const hasNegativeTotal = total < 0;

    const sendDisabledTooltip = getSendDisabledTooltip({
        hasDueDateError: saveDisabled,
        hasRecipient: toEmails.length > 0,
        hasSubject: Boolean(state.emailSubject.trim()),
        scheduledSendStatus,
    });

    // Saving is part of the loading state so the button shows a spinner while
    // handleSend awaits saver.save() (rather than a bare disabled button); it
    // therefore stays out of sendDisabledTooltip, which is left undefined for
    // transient states the spinner already explains.
    const isSending =
        sendNativeInvoice.isPending ||
        scheduleNativeInvoice.isPending ||
        saver.status === "saving";

    const sendDisabled = sendDisabledTooltip !== undefined || isSending;

    return {
        handleDownload,
        handleSend,
        saveDisabled,
        hasNegativeTotal,
        sendDisabled,
        isInvoiceDateLocked,
        sendDisabledTooltip,
        isDownloading,
        isSending,
    };
}
